import { useCallback, useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { UserContext } from '../../context';
import { EventActivityStatus, EventOrderBy, EventStatus } from '../../services';
import { getEventsFeed, loadMoreEventsFeed } from '../../slices';
import { EVENTS_FEED_ADMIN_COOKIE_KEY, EVENTS_FEED_COOKIE_KEY } from '../constants';
import { OrderDirection } from '../models';
import { isAdminLayout } from '../utils';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useLastDate } from './useLastDate';

interface EventsFeedArgs {
  organisationTagIds?: number[];
  query?: string;
}

export const useEventsFeed = ({ organisationTagIds = [], query }: EventsFeedArgs = {}) => {
  const dispatch = useAppDispatch();

  const { channelId } = useContext(UserContext).userInfo.userData;

  const cookieKey = isAdminLayout() ? EVENTS_FEED_ADMIN_COOKIE_KEY : EVENTS_FEED_COOKIE_KEY;

  const [cookies] = useCookies([cookieKey]);

  const { getDateRange } = useLastDate();

  const { page } = useAppSelector(({ eventsFeed }) => eventsFeed);

  // react-cookie returns a new `cookies` reference on every write regardless of which key changed.
  // Serialising stabilises the dep so unrelated writes don't invalidate getEventsFeedItems.
  const eventsFeedCookieJson = useMemo(
    () => JSON.stringify(cookies[cookieKey]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cookies]
  );

  const { orderBy, orderDirection, requestFilters } = useMemo(() => {
    const {
      requestFilters = {},
      orderBy = EventOrderBy.START_AT,
      orderDirection = OrderDirection.ASC,
    } = JSON.parse(eventsFeedCookieJson ?? '{}') ?? {};

    return { orderBy, orderDirection, requestFilters };
  }, [eventsFeedCookieJson]);

  const filters = useMemo(() => {
    const { dateRange, statuses, activityStatuses, ...rest } = requestFilters;

    return {
      ...rest,
      ...getDateRange(dateRange),
      statuses: isAdminLayout() ? statuses : [EventStatus.PUBLISHED, ...(statuses ?? [])],
      activityStatuses: [EventActivityStatus.UPCOMING, ...(activityStatuses ?? [])],
      organisationTagIds,
      query,
    };
  }, [requestFilters, getDateRange, organisationTagIds, query]);

  const getEventsFeedItems = useCallback(
    () => dispatch(getEventsFeed({ channelId, filters, orderBy, orderDirection })),
    [channelId, dispatch, filters, orderBy, orderDirection]
  );

  const loadMoreEventsFeedItems = useCallback(
    () =>
      dispatch(loadMoreEventsFeed({ channelId, page: page + 1, filters, orderBy, orderDirection })),
    [channelId, dispatch, filters, orderBy, orderDirection, page]
  );

  return { getEventsFeedItems, loadMoreEventsFeedItems };
};
